import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import { storeToRefs } from 'pinia';
import { useAuthStore } from '~/auth/stores/auth.store';
import { useBiStore } from '~/bi/store/bi.store';
import { useCommonImports } from '~/common/composables/common-imports.composable';
import { useCommonStore } from '~/common/stores/common.store';
import { sleep } from '~/common/utils/common.utils';
import { load_js_css_file } from '~/common/utils/load-script.util';

// Heavily inspired by https://github.com/metabase/metabase/blob/master/frontend/src/metabase/visualizations/lib/save-dashboard-pdf.ts (especially the page break logic)
// Scope for improvement:
// 1. The export used to be quick at the beginning but currently is a bit slow.
// 2. Refactor exportDashboard and exportWidget to avoid too much duplication.

// The exportDashboard function runs even for scheduled dashboard exports because the backend opens the application (dashboard build) on a headless browser (browserless) with the query `print=true` and `scheduled_export={UID}` to get the exportable PDF. Backend sends this captured PDF via email.
// Example link used by the backend: (Replace the token with your JWT token for testing, backend actually uses BOT_TOKEN from its env variables. Contact Tebby/Saideep if you need it)
// https://dashboard.dev-taskmapper.com/Uf7ChuHfSK/bi-dashboard?token=TOKEN&organization=00g11bpc8113qSzA30h8&dashboard=EKqwkJslD&print=true&scheduled_export=b5e5133d
// Note that the dashboard build is used for public URLs also like the below:
// https://dashboard.dev-taskmapper.com/Uf7ChuHfSK/bi-dashboard/public?public_token=_JSaEfAfU&organization=00g11bpc8113qSzA30h8&dashboard=EKqwkJslD
// You can run the dashboard build locally using the command `pnpm dev --mode widget-dashboard-development`

const TASKMAPPER_LOGO = `
  <svg width="763" height="141" viewBox="0 0 763 141" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14.47 140.9C10.7101 140.898 7.10488 139.403 4.44619 136.744C1.7875 134.085 0.292688 130.48 0.290039 126.72V43.1702C0.290039 39.4095 1.78399 35.8027 4.44326 33.1435C7.10253 30.4842 10.7093 28.9902 14.47 28.9902C18.2308 28.9902 21.8375 30.4842 24.4968 33.1435C27.1561 35.8027 28.65 39.4095 28.65 43.1702V126.72C28.6474 130.48 27.1526 134.085 24.4939 136.744C21.8352 139.403 18.23 140.898 14.47 140.9Z" fill="url(#paint0_linear_201_1194)"/>
    <path d="M59.3299 112.28C55.57 112.277 51.9648 110.783 49.3061 108.124C46.6474 105.465 45.1526 101.86 45.1499 98.1001V14.5501C45.1499 12.688 45.5167 10.8441 46.2293 9.12367C46.9419 7.40327 47.9864 5.84008 49.3031 4.52334C50.6199 3.20661 52.1831 2.16212 53.9035 1.4495C55.6239 0.736893 57.4678 0.370117 59.3299 0.370117C61.1921 0.370117 63.036 0.736893 64.7564 1.4495C66.4768 2.16212 68.04 3.20661 69.3567 4.52334C70.6734 5.84008 71.7179 7.40327 72.4305 9.12367C73.1431 10.8441 73.5099 12.688 73.5099 14.5501V98.1001C73.5073 101.86 72.0125 105.465 69.3538 108.124C66.6951 110.783 63.0899 112.277 59.3299 112.28Z" fill="url(#paint1_linear_201_1194)"/>
    <path d="M150.49 38H123V29H187.24V38H159.77V112.31H150.49V38ZM248.63 74.12V112.31H239.71V102.49C235.31 109.89 226.15 113.76 218.06 113.76C205.06 113.76 195.57 106.13 195.57 93.76C195.57 81.39 206.52 72.91 219.73 72.91C226.539 73.0396 233.286 74.2271 239.73 76.43V74.12C239.73 65.64 236.4 55.94 223.08 55.94C216.89 55.94 210.7 58.73 205.08 61.64L201.51 54.24C210.32 49.75 217.69 48.18 223.64 48.18C240.43 48.15 248.63 58.94 248.63 74.09V74.12ZM239.71 92.67V83.09C233.668 81.4036 227.423 80.549 221.15 80.55C211.99 80.55 204.26 85.88 204.26 93.4C204.26 101.16 210.92 105.76 219.61 105.76C227.7 105.73 237.33 101.49 239.71 92.64V92.67ZM257 104.4L261.64 97.73C268.64 102.95 275.64 105.61 282.46 105.61C290.55 105.61 296.02 101.73 296.02 95.79C296.02 89 288.88 86.79 280.55 84.4C265.68 80.03 259.97 75.55 259.97 66.58C259.97 55.18 269.25 48.15 281.51 48.15C289.366 48.3047 297.009 50.7364 303.51 55.15L299.22 62.15C293.04 58.15 286.97 56.21 281.5 56.21C274.6 56.21 268.89 59.36 268.89 65.42C268.89 70.88 273.41 72.58 284.83 76.34C294.7 79.49 304.93 82.88 304.93 94.76C304.93 106.28 295.18 113.67 282.45 113.67C273.166 113.53 264.199 110.264 257 104.4ZM339.56 78.33L365.73 112.33H354.51L333.21 84.57L322.15 95.72V112.33H313.23V27.54H322.15V84.28L355.82 49.6H367.24L339.56 78.33Z" fill="#130C0E"/>
    <path d="M377.1 29H386.38L416.71 87.07L447.05 29H456.33V112.29H447.05V49.36L416.71 107.43L386.38 49.36V112.28H377.1V29ZM517.78 74.1V112.29H508.86V102.47C504.46 109.87 495.3 113.74 487.21 113.74C474.21 113.74 464.72 106.11 464.72 93.74C464.72 81.37 475.67 72.89 488.87 72.89C495.679 73.0197 502.426 74.2072 508.87 76.41V74.1C508.87 65.62 505.54 55.92 492.21 55.92C486.03 55.92 479.84 58.71 474.21 61.62L470.64 54.22C479.45 49.73 486.82 48.16 492.77 48.16C509.57 48.15 517.78 58.94 517.78 74.09V74.1ZM508.86 92.65V83.07C502.818 81.3835 496.573 80.529 490.3 80.53C481.14 80.53 473.41 85.86 473.41 93.38C473.41 101.14 480.07 105.74 488.75 105.74C496.84 105.73 506.48 101.49 508.86 92.64V92.65ZM585.71 80.89C585.71 100.17 573.58 113.74 557.4 113.74C552.81 113.817 548.288 112.623 544.335 110.29C540.382 107.957 537.151 104.576 535 100.52V133.13H526.08V49.6H535V61.36C537.152 57.3063 540.384 53.9269 544.337 51.5955C548.29 49.2641 552.811 48.0716 557.4 48.15C573.58 48.15 585.71 61.73 585.71 80.88V80.89ZM576.31 80.89C576.31 66.46 567.75 56.28 555.73 56.28C543.71 56.28 535 66.45 535 80.88C535 95.31 543.57 105.61 555.7 105.61C567.83 105.61 576.31 95.31 576.31 80.88V80.89ZM653.64 80.89C653.64 100.17 641.5 113.74 625.32 113.74C620.744 113.803 616.239 112.602 612.301 110.269C608.363 107.937 605.145 104.563 603 100.52V133.13H594V49.6H603V61.36C605.149 57.3124 608.374 53.937 612.32 51.606C616.265 49.275 620.778 48.0791 625.36 48.15C641.5 48.15 653.64 61.73 653.64 80.88V80.89ZM644.24 80.89C644.24 66.46 635.67 56.28 623.66 56.28C611.65 56.28 603 66.45 603 80.88C603 95.31 611.56 105.61 623.7 105.61C635.84 105.61 644.24 95.31 644.24 80.88V80.89ZM712.41 98.11L717.88 104.05C712.52 109.99 701.7 113.74 693.13 113.74C676.71 113.74 661.96 100.05 661.96 80.74C661.96 62.07 675.76 48.13 692.3 48.13C710.86 48.13 721.45 62.43 721.45 84.38H671.12C672.43 96.62 680.88 105.59 693.01 105.59C700.27 105.61 708.6 102.34 712.41 98.1V98.11ZM671.24 76.28H712.64C711.45 65.01 704.91 56.28 692.64 56.28C681.71 56.27 672.91 64.15 671.24 76.27V76.28ZM762.58 48.16V56.28C748.66 56.28 738.79 65.01 738.79 77.28V112.28H729.86V49.6H738.79V61.85C742.47 53.48 751.28 48.15 762.58 48.15V48.16Z" fill="url(#paint2_linear_201_1194)"/>
    <defs>
      <linearGradient id="paint0_linear_201_1194" x1="14.47" y1="27.8802" x2="14.47" y2="138.74" gradientUnits="userSpaceOnUse">
        <stop stop-color="#2782C5"/>
        <stop offset="1" stop-color="#39A0A7"/>
      </linearGradient>
      <linearGradient id="paint1_linear_201_1194" x1="59.3299" y1="-0.739882" x2="59.3299" y2="110.12" gradientUnits="userSpaceOnUse">
        <stop stop-color="#3DA89E"/>
        <stop offset="1" stop-color="#45BA86"/>
      </linearGradient>
      <linearGradient id="paint2_linear_201_1194" x1="375.05" y1="81.06" x2="761.63" y2="81.06" gradientUnits="userSpaceOnUse">
        <stop stop-color="#2A81C4"/>
        <stop offset="1" stop-color="#4BBB86"/>
      </linearGradient>
    </defs>
  </svg>
`;

function yieldToMain() {
  if (typeof scheduler !== 'undefined' && typeof scheduler.yield === 'function') {
    return scheduler.yield();
  }
  return new Promise(resolve => setTimeout(resolve, 0));
}

// Match the row header overlay (serial number column) row heights with the master table rows
function syncRowHeaderHeights(root) {
  const masters = root.querySelectorAll('.ht_master');
  masters.forEach((master) => {
    const overlay = master.parentElement?.querySelector(':scope > .ht_clone_inline_start');
    if (!overlay)
      return;

    const masterRows = master.querySelectorAll('.htCore > tbody > tr');
    const overlayRows = overlay.querySelectorAll('.htCore > tbody > tr');
    const count = Math.min(masterRows.length, overlayRows.length);
    const heights = [];
    for (let i = 0; i < count; i++) {
      heights.push(masterRows[i].getBoundingClientRect().height);
    }
    for (let i = 0; i < count; i++) {
      const height = `${heights[i]}px`;
      overlayRows[i].style.height = height;
      overlayRows[i].querySelectorAll('th, td').forEach((cell) => {
        cell.style.boxSizing = 'border-box';
        cell.style.height = height;
      });
    }
  });
}

const PAGE_PADDING = 0;
const HEADER_HEIGHT = 110;
const HEADER_MARGIN_BOTTOM = 0;
const TARGET_ASPECT_RATIO = 21 / 17;

export function useBiExport() {
  const { $t } = useCommonImports();
  const bi_store = useBiStore();
  const auth_store = useAuthStore();
  const common_store = useCommonStore();

  const { selected_dashboard_details } = storeToRefs(bi_store);

  // uid: [Required] Pass the widget id if format is not null, else pass the element
  // widget_name: [Conditional] Required for pdf and png export
  // format: 'pdf', 'png', null
  async function exportWidget(uid, widget_name, format) {
    await load_js_css_file(
      'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js',
      'html2canvas-js',
      'js',
    );

    const targeted_element = document.querySelector(`[uid="${uid}"]`) || document.getElementById(`${uid}`);

    // Force all Handsontable instances to render every column so the full table is in the DOM
    const widgetHtInstancesToRestore = [];
    const htContainerEls = Array.from(targeted_element.querySelectorAll('[id^="bi-table-widget-"]'));
    for (const el of htContainerEls) {
      try {
        const instance = window.__biHotInstances?.get(el.id);
        if (instance) {
          widgetHtInstancesToRestore.push(instance);
          instance.updateSettings({ renderAllColumns: true, renderAllRows: true });
        }
      }
      catch { /* instance may not be available */ }
    }

    const htHolders = targeted_element.querySelectorAll('.ht_master .wtHolder');
    if (htHolders.length) {
      htHolders.forEach((holder) => {
        holder.scrollTop = 0;
        holder.scrollLeft = 0;
      });
    }

    if (widgetHtInstancesToRestore.length || htHolders.length) {
      await sleep(500);
    }

    // Measure full table scroll widths now that all columns are rendered
    let captureWidth = targeted_element.offsetWidth;
    const masterHolders = Array.from(targeted_element.querySelectorAll('.ht_master .wtHolder'));
    masterHolders.forEach((holder) => {
      const extraWidth = holder.scrollWidth - holder.clientWidth;
      if (extraWidth > 0) {
        captureWidth = Math.max(captureWidth, targeted_element.offsetWidth + extraWidth);
      }
    });

    // eslint-disable-next-line no-undef
    const canvas = await html2canvas(targeted_element, {
      scale: 2,
      backgroundColor: '#ffffff',
      useCORS: true,
      allowTaint: true,
      imageTimeout: 0,
      width: captureWidth,
      height: targeted_element.offsetHeight,
      onclone: (cloned_document) => {
        const widgetTitleElements = cloned_document.querySelectorAll('.widget-title');
        widgetTitleElements.forEach((title) => {
          title.style.marginTop = '-12px';
        });

        const fontSemiboldElements = cloned_document.querySelectorAll('.font-semibold');
        fontSemiboldElements.forEach((title) => {
          title.style.marginTop = '-12px';
          title.style.marginBottom = '8px';
        });

        const proseMirrorElements = cloned_document.querySelectorAll('.ProseMirror');
        proseMirrorElements.forEach((title) => {
          title.style.marginTop = '-6px';
        });

        const allElements = cloned_document.querySelectorAll('*');
        Array.from(allElements).forEach((element) => {
          element.style.overflow = 'visible';
          element.style.textOverflow = 'clip';
        });

        const tds = cloned_document.getElementsByTagName('td');
        Array.from(tds).forEach((td) => {
          td.firstChild.style.marginTop = '-14px';
        });

        const ths = cloned_document.getElementsByTagName('th');
        Array.from(ths).forEach((th) => {
          if (th.getAttribute('scope') === 'row') {
            th.firstChild.style.marginTop = '-4px';
            return;
          }
          th.firstChild.style.marginTop = '-8px';
        });

        const tableTagNodes = cloned_document.querySelectorAll('.table-tag');
        tableTagNodes.forEach((tag) => {
          tag.style.marginTop = '14px';
          const span = document.createElement('span');
          span.style.position = 'relative';
          span.style.top = '-6px';
          span.style.display = 'inline-block';
          while (tag.firstChild) {
            span.appendChild(tag.firstChild);
          }
          tag.appendChild(span);
        });

        const changeTypeButtons = cloned_document.querySelectorAll('.changeType');
        changeTypeButtons.forEach((button) => {
          button.style.display = 'none';
        });

        const htPageNavigationSections = cloned_document.querySelectorAll('.ht-page-navigation-section');
        htPageNavigationSections.forEach((section) => {
          section.style.display = 'none';
        });

        const htPageSizeSections = cloned_document.querySelectorAll('.ht-page-size-section');
        htPageSizeSections.forEach((section) => {
          section.style.display = 'none';
        });

        const htPageCounterSections = cloned_document.querySelectorAll('.ht-page-counter-section');
        htPageCounterSections.forEach((section) => {
          section.style.marginTop = '-10px';
          section.style.paddingTop = '8px';
          section.style.paddingBottom = '8px';
        });

        const collapsibleElements = cloned_document.querySelectorAll('.collapsibleIndicator');
        collapsibleElements.forEach((el) => {
          el.style.display = 'none';
        });

        const svgs = cloned_document.querySelectorAll('svg');
        Array.from(svgs).forEach((svg) => {
          if (svg.classList.contains('bi-exportable-icon')) {
            svg.style.marginTop = '16px';
          }
        });

        const exportable_tags = cloned_document.querySelectorAll('.bi-exportable-tag');
        exportable_tags.forEach((tag) => {
          tag.style.marginTop = '14px';
          tag.style.minHeight = '14px';

          const children = tag.querySelectorAll('div');
          children.forEach((child) => {
            child.style.marginTop = '-14px';
          });
        });

        const exportable_texts = cloned_document.querySelectorAll('.bi-exportable-text');
        exportable_texts.forEach((text) => {
          text.style.marginTop = '-14px';
        });

        const quickFilterTabs = cloned_document.querySelectorAll('.bi-widget-quick-filters .border-b-2');
        quickFilterTabs.forEach((tab) => {
          tab.style.height = '26px';
          tab.style.borderBottomColor = 'transparent';
        });

        const quickFilterDropdowns = cloned_document.querySelectorAll('.bi-widget-quick-filters .px-2\\.5');
        quickFilterDropdowns.forEach((dropdown) => {
          dropdown.style.height = '26px';
        });

        syncRowHeaderHeights(cloned_document);
      },
    });

    // Restore Handsontable virtualization settings after capture
    for (const instance of widgetHtInstancesToRestore) {
      try {
        instance.updateSettings({ renderAllColumns: false, renderAllRows: false });
      }
      catch { /* instance may have been destroyed */ }
    }

    const imgData = canvas.toDataURL('image/png', 1.0);

    if (format === 'pdf') {
      const img = new Image();
      img.onload = () => {
        const margin = 20;
        const pdfWidth = img.width + (margin * 2);
        const pdfHeight = img.height + (margin * 2);

        // eslint-disable-next-line new-cap
        const pdf = new jsPDF({
          orientation: pdfWidth > pdfHeight ? 'landscape' : 'portrait',
          unit: 'px',
          format: [pdfWidth, pdfHeight],
          compress: true,
        });
        pdf.addImage(imgData, 'PNG', margin, margin, img.width, img.height);
        pdf.save(`${widget_name || 'Untitled'}.pdf`);
      };
      img.src = imgData;
    }
    else if (format === 'png') {
      const a = document.createElement('a');
      a.href = imgData;
      a.download = `${widget_name || 'Untitled'}.png`;
      a.click();
    }
    else {
      return imgData;
    }
  }

  function getSortedWidgetBounds(gridNode, dashboard_layout) {
    const widgets = Array.from(gridNode.querySelectorAll('.vgl-item:not(.vgl-item--placeholder)'));
    const parentOffset = gridNode.getBoundingClientRect().top;

    return widgets
      .map((widget) => {
        const rect = widget.getBoundingClientRect();
        const uid = widget.getAttribute('uid');
        const layout = dashboard_layout.find(item => item.uid === uid);

        return {
          element: widget,
          uid,
          layout,
          top: Math.round(rect.top - parentOffset),
          bottom: Math.round(rect.bottom - parentOffset),
          height: Math.round(rect.height),
        };
      })
      .filter(w => w.layout)
      .sort((a, b) => {
        if (a.layout.y !== b.layout.y) {
          return a.layout.y - b.layout.y;
        }
        return a.layout.x - b.layout.x;
      });
  }

  function findPageBreakCandidates(widgetBounds) {
    if (widgetBounds.length === 0) {
      return [];
    }

    const maxBottom = Math.max(...widgetBounds.map(w => w.bottom));
    const possibleBreaks = new Set(
      widgetBounds
        .filter(w => w.bottom < maxBottom)
        .map(w => w.bottom),
    );

    for (const widget of widgetBounds) {
      for (const breakCandidate of Array.from(possibleBreaks)) {
        const isWithinWidget = breakCandidate > widget.top && breakCandidate < widget.bottom;
        if (isWithinWidget) {
          possibleBreaks.delete(breakCandidate);
        }
      }
    }

    return Array.from(possibleBreaks).sort((a, b) => a - b);
  }

  function getPageBreaks(widgetBounds, optimalPageHeight, totalHeight, minPageHeight, verticalOffset) {
    if (widgetBounds.length === 0) {
      return [];
    }

    const pageBreakCandidates = findPageBreakCandidates(widgetBounds);
    const adjustedOptimalHeight = optimalPageHeight - verticalOffset;
    const canFitInOnePage = adjustedOptimalHeight >= totalHeight;

    if (pageBreakCandidates.length === 0 || canFitInOnePage) {
      return [];
    }

    const result = [];
    let currentPageStart = 0;
    let candidateIndex = 0;

    while (currentPageStart < totalHeight) {
      const targetBreakPoint = currentPageStart + adjustedOptimalHeight;

      while (
        candidateIndex < pageBreakCandidates.length
        && pageBreakCandidates[candidateIndex] <= currentPageStart + minPageHeight
      ) {
        candidateIndex++;
      }

      if (candidateIndex >= pageBreakCandidates.length) {
        break;
      }

      let bestBreak = pageBreakCandidates[candidateIndex];

      if (candidateIndex + 1 < pageBreakCandidates.length) {
        const nextBreak = pageBreakCandidates[candidateIndex + 1];
        const currentDiff = Math.abs(targetBreakPoint - bestBreak);
        const nextDiff = Math.abs(targetBreakPoint - nextBreak);
        if (nextDiff < currentDiff) {
          bestBreak = nextBreak;
          candidateIndex++;
        }
      }

      result.push(bestBreak + verticalOffset);
      currentPageStart = bestBreak;
    }

    return result;
  }

  async function createHeaderElement(signal) {
    const current_organization = auth_store.current_organization;
    const current_asset = common_store.active_asset;

    const header = document.createElement('div');
    header.style.cssText = `
      width: calc(100% - 16px);
      height: ${HEADER_HEIGHT}px;
      background-color: #ffffff;
      padding: 16px 16px 16px 16px;
      box-sizing: border-box;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      border-bottom: 1px solid #d9d9d9;
      margin-bottom: ${HEADER_MARGIN_BOTTOM}px;
    `;

    const leftSide = document.createElement('div');
    leftSide.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 8px;
    `;

    const logoContainer = document.createElement('div');
    if (current_organization.cover_image?.url) {
      const img = document.createElement('img');
      img.crossOrigin = 'Anonymous';
      img.style.cssText = 'width: 96; height: 40px; object-fit: contain;';

      await new Promise((resolve, reject) => {
        const abortHandler = () => reject(new Error('Export cancelled by user'));
        signal?.addEventListener('abort', abortHandler);

        img.onload = () => {
          signal?.removeEventListener('abort', abortHandler);
          resolve();
        };
        img.onerror = () => {
          signal?.removeEventListener('abort', abortHandler);
          resolve();
        };

        const url = new URL(current_organization.cover_image.url);
        url.searchParams.set('t', Date.now());
        img.src = url.toString();
      });

      logoContainer.appendChild(img);
    }
    else {
      logoContainer.innerHTML = TASKMAPPER_LOGO;
      logoContainer.style.cssText = 'width: 100px; height: 30px;';
      const svg = logoContainer.querySelector('svg');
      if (svg) {
        svg.style.cssText = 'width: 100%; height: 100%;';
      }
    }
    leftSide.appendChild(logoContainer);

    const title = document.createElement('div');
    title.style.cssText = `
      font-family: "Inter var", Arial, sans-serif;
      font-size: 16px;
      font-weight: 700;
      color: #101828;
      margin-top: 4px;
    `;
    title.textContent = selected_dashboard_details.value.name;
    leftSide.appendChild(title);

    header.appendChild(leftSide);

    const rightSide = document.createElement('div');
    rightSide.style.cssText = `
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        justify-content: space-between;
        padding-bottom: 2px;
        text-align: right;
        height: 100%;
      `;

    const assetInfo = document.createElement('div');
    assetInfo.style.cssText = `
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 0px;
      `;

    const assetName = document.createElement('div');
    assetName.style.cssText = `
        font-family: "Inter var", Arial, sans-serif;
        font-size: 12px;
        font-weight: 600;
        color: #101828;
      `;
    assetName.textContent = current_asset?.name || '';
    assetInfo.appendChild(assetName);

    const assetAddress = document.createElement('div');
    assetAddress.style.cssText = `
        font-family: "Inter var", Arial, sans-serif;
        font-size: 10px;
        color: #475467;
      `;
    assetAddress.textContent = current_asset?.address?.name || '';
    assetInfo.appendChild(assetAddress);
    rightSide.appendChild(assetInfo);

    const generatedOn = document.createElement('div');
    generatedOn.style.cssText = `
        font-family: "Inter var", Arial, sans-serif;
        font-size: 10px;
        color: #475467;
      `;
    generatedOn.textContent = `${$t('Generated on')}: ${dayjs().format('DD MMMM YYYY, hh:mm A z')}`;
    rightSide.appendChild(generatedOn);

    header.appendChild(rightSide);

    return header;
  }

  async function exportDashboard(dashboard_layout, abort_controller = null) {
    const controller = abort_controller;
    const signal = controller?.signal;
    const htInstancesToRestore = [];
    const savedStyles = [];

    try {
      if (signal?.aborted) {
        throw new Error('Export cancelled by user');
      }

      await load_js_css_file(
        'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js',
        'html2canvas-js',
        'js',
      );

      if (signal?.aborted) {
        throw new Error('Export cancelled by user');
      }

      const bi_export_container = document.querySelector('#bi-fullscreen-container');
      if (!bi_export_container) {
        console.error('Grid container not found');
        return;
      }

      const widgetBounds = getSortedWidgetBounds(bi_export_container, dashboard_layout);

      const pdfHeader = await createHeaderElement(signal);

      // Force all Handsontable instances to render every column so the full table is in the DOM
      const htContainerEls = Array.from(bi_export_container.querySelectorAll('[id^="bi-table-widget-"]'));
      for (const el of htContainerEls) {
        try {
          const instance = window.__biHotInstances?.get(el.id);
          if (instance) {
            htInstancesToRestore.push(instance);
            instance.updateSettings({ renderAllColumns: true, renderAllRows: true });
          }
        }
        catch { /* instance may not be available */ }
      }

      // Reset scroll positions on all Handsontable holders
      const allHtMasterHolders = bi_export_container.querySelectorAll('.ht_master .wtHolder');
      allHtMasterHolders.forEach((holder) => {
        holder.scrollTop = 0;
        holder.scrollLeft = 0;
      });

      if (htInstancesToRestore.length || allHtMasterHolders.length) {
        await sleep(500);
      }

      // Measure full table scroll widths now that all columns are rendered
      const originalMasterHolders = Array.from(bi_export_container.querySelectorAll('.ht_master .wtHolder'));
      const masterHolderScrollWidths = originalMasterHolders.map(h => h.scrollWidth);
      let maxTableOverflow = 0;
      originalMasterHolders.forEach((holder) => {
        maxTableOverflow = Math.max(maxTableOverflow, holder.scrollWidth - holder.clientWidth);
      });

      // ── Expand the LIVE DOM so the browser re-flows the layout before html2canvas clones it ──
      if (maxTableOverflow > 0) {
        originalMasterHolders.forEach((holder, index) => {
          const scrollW = masterHolderScrollWidths[index];
          if (scrollW <= holder.clientWidth)
            return;

          const fullWidthPx = `${scrollW}px`;

          // Walk from holder up to the grid item (.vgl-item) and expand every ancestor
          let el = holder;
          while (el && el !== bi_export_container) {
            savedStyles.push({ el, width: el.style.width, maxWidth: el.style.maxWidth, minWidth: el.style.minWidth, overflow: el.style.overflow });
            el.style.width = fullWidthPx;
            el.style.maxWidth = 'none';
            el.style.minWidth = fullWidthPx;
            el.style.overflow = 'visible';
            el = el.parentElement;
          }

          // Also expand .wtHider and reset .wtSpreader position
          const hider = holder.querySelector('.wtHider');
          if (hider) {
            savedStyles.push({ el: hider, width: hider.style.width, maxWidth: hider.style.maxWidth, minWidth: hider.style.minWidth });
            hider.style.width = fullWidthPx;
            hider.style.maxWidth = 'none';
            hider.style.minWidth = fullWidthPx;
          }
          const spreader = holder.querySelector('.wtSpreader');
          if (spreader) {
            savedStyles.push({ el: spreader, left: spreader.style.left, position: spreader.style.position });
            spreader.style.left = '0px';
            spreader.style.position = 'relative';
          }

          // Hide non-master overlay holders to avoid duplicate content
          const htRoot = holder.closest('.ht-theme-main') || holder.closest('.handsontable');
          if (htRoot) {
            htRoot.querySelectorAll('.wtHolder').forEach((h) => {
              if (!h.closest('.ht_master')) {
                savedStyles.push({ el: h, overflow: h.style.overflow, display: h.style.display });
                h.style.overflow = 'hidden';
              }
            });
          }
        });

        // Expand the export container itself
        savedStyles.push({ el: bi_export_container, width: bi_export_container.style.width, maxWidth: bi_export_container.style.maxWidth, minWidth: bi_export_container.style.minWidth });
        const fullContentWidth = bi_export_container.offsetWidth + maxTableOverflow;
        bi_export_container.style.width = `${fullContentWidth}px`;
        bi_export_container.style.maxWidth = 'none';
        bi_export_container.style.minWidth = `${fullContentWidth}px`;

        // Let the browser re-layout with the expanded widths
        await sleep(300);
      }

      const contentWidth = bi_export_container.offsetWidth;
      const width = contentWidth + PAGE_PADDING * 2;

      const verticalOffset = HEADER_HEIGHT + HEADER_MARGIN_BOTTOM;
      const gridHeight = bi_export_container.offsetHeight;
      const contentHeight = gridHeight + verticalOffset;

      const optimalPageHeight = Math.round(width * TARGET_ASPECT_RATIO);
      const minPageHeight = contentWidth;
      const pageBreaks = getPageBreaks(
        widgetBounds,
        optimalPageHeight,
        gridHeight,
        minPageHeight,
        verticalOffset,
      );

      if (signal?.aborted) {
        throw new Error('Export cancelled by user');
      }

      const backgroundColor = '#ffffff';
      const exportScale = window.devicePixelRatio || 2;

      // eslint-disable-next-line no-undef
      const image = await html2canvas(bi_export_container, {
        height: contentHeight,
        width: contentWidth,
        useCORS: true,
        allowTaint: true,
        backgroundColor,
        scale: exportScale,
        imageTimeout: 0,
        logging: false,
        onclone: async (_doc, node) => {
          node.style.height = `${contentHeight}px`;
          node.style.backgroundColor = backgroundColor;

          const allElements = node.querySelectorAll('*');
          Array.from(allElements).forEach((element) => {
            element.style.overflow = 'visible';
            element.style.textOverflow = 'clip';
          });
          await yieldToMain();

          // Keep non-master overlay holders hidden to avoid duplicate header content
          const allWtHolders = node.querySelectorAll('.wtHolder');
          allWtHolders.forEach((holder) => {
            if (!holder.closest('.ht_master')) {
              holder.style.overflow = 'hidden';
            }
          });

          const detailsCardWidgets = node.querySelectorAll('.bi-details-card-widget');
          detailsCardWidgets.forEach((widget) => {
            widget.style.overflow = 'hidden';
          });

          const richTextWidgets = node.querySelectorAll('.bi-rich-text-widget');
          richTextWidgets.forEach((widget) => {
            widget.style.marginTop = '-4px';
            widget.style.overflow = 'hidden';
          });

          const verticallyCenteredRichTextWidgets = node.querySelectorAll('.bi-vertically-centered-rich-text-widget');
          verticallyCenteredRichTextWidgets.forEach((widget) => {
            widget.style.marginTop = '-7px';
            widget.style.overflow = 'hidden';
          });

          const widgetTitleElements = node.querySelectorAll('.widget-title');
          widgetTitleElements.forEach((title) => {
            title.style.marginTop = '-12px';
            title.style.overflow = 'hidden';
            title.style.height = '26px';
            title.style.textOverflow = 'ellipsis';
          });

          const fontSemiboldElements = node.querySelectorAll('.font-semibold');
          fontSemiboldElements.forEach((title) => {
            title.style.marginTop = '-12px';
            title.style.marginBottom = '8px';
          });

          const proseMirrorElements = node.querySelectorAll('.ProseMirror');
          proseMirrorElements.forEach((title) => {
            title.style.marginTop = '-6px';
          });

          const tds = node.getElementsByTagName('td');
          Array.from(tds).forEach((td) => {
            if (td.firstChild) {
              td.firstChild.style.marginTop = '-14px';
            }
          });

          const ths = node.getElementsByTagName('th');
          Array.from(ths).forEach((th) => {
            if (th.firstChild) {
              if (th.getAttribute('scope') === 'row') {
                th.firstChild.style.marginTop = '-4px';
              }
              else {
                th.firstChild.style.marginTop = '-8px';
              }
            }
          });

          const tableTagNodes = node.querySelectorAll('.table-tag');
          tableTagNodes.forEach((tag) => {
            tag.style.marginTop = '14px';
            const span = document.createElement('span');
            span.style.position = 'relative';
            span.style.top = '-6px';
            span.style.display = 'inline-block';
            while (tag.firstChild) {
              span.appendChild(tag.firstChild);
            }
            tag.appendChild(span);
          });

          const changeTypeButtons = node.querySelectorAll('.changeType');
          changeTypeButtons.forEach((button) => {
            button.style.display = 'none';
          });

          const htPageNavigationSections = node.querySelectorAll('.ht-page-navigation-section');
          htPageNavigationSections.forEach((section) => {
            section.style.display = 'none';
          });

          const htPageSizeSections = node.querySelectorAll('.ht-page-size-section');
          htPageSizeSections.forEach((section) => {
            section.style.display = 'none';
          });

          const htPageCounterSections = node.querySelectorAll('.ht-page-counter-section');
          htPageCounterSections.forEach((section) => {
            section.style.marginTop = '-10px';
            section.style.paddingTop = '8px';
            section.style.paddingBottom = '8px';
          });

          node.querySelectorAll('.collapsibleIndicator').forEach((el) => {
            const isExpanded = el.classList.contains('expanded');

            const style = getComputedStyle(el);
            const iconSize = style.getPropertyValue('--ht-icon-size')?.trim() || '16px';
            const lineHeight = style.getPropertyValue('--ht-line-height')?.trim() || '20px';
            const gap = style.getPropertyValue('--ht-gap-size')?.trim() || '4px';
            const radius = style.getPropertyValue('--ht-collapse-button-border-radius')?.trim() || '2px';
            const borderColor
              = isExpanded ? style.getPropertyValue('--ht-collapse-button-open-border-color')?.trim() : style.getPropertyValue('--ht-collapse-button-close-border-color')?.trim() || '#999';
            const bgColor
              = isExpanded ? style.getPropertyValue('--ht-collapse-button-open-background-color')?.trim() : style.getPropertyValue('--ht-collapse-button-close-background-color')?.trim() || '#fff';
            const iconColor
              = isExpanded ? style.getPropertyValue('--ht-collapse-button-open-icon-color')?.trim() : style.getPropertyValue('--ht-collapse-button-close-icon-color')?.trim() || '#333';

            const fakeIcon = document.createElement('div');
            // The below symbol is em-dash intentionally
            fakeIcon.textContent = isExpanded ? '–' : '+';

            fakeIcon.style.cssText = `
              display: inline-flex;
              align-items: center;
              justify-content: center;
              width: ${iconSize};
              height: ${iconSize};
              border: 1px solid ${borderColor};
              border-radius: ${radius};
              background-color: ${bgColor};
              color: ${iconColor};
              font-size: 12px;
              font-weight: 600;
              line-height: 1;
              box-sizing: border-box;
              cursor: pointer;
              float: right;
              margin-inline-end: 1px;
              margin-inline-start: ${gap};
              position: relative;
              top: calc((${lineHeight} - ${iconSize}) / 2);
              overflow: hidden;
              margin-top: 7px;
            `;

            fakeIcon.style.paddingBottom = '15px';

            el.replaceWith(fakeIcon);
          });

          // const collapsibleElements = node.querySelectorAll('.collapsibleIndicator');
          // collapsibleElements.forEach((el) => {
          //   el.style.display = 'none';
          // });

          const svgs = node.querySelectorAll('svg');
          Array.from(svgs).forEach((svg) => {
            if (svg.classList.contains('bi-exportable-icon')) {
              svg.style.marginTop = '16px';
            }
          });

          const non_exportable_elements = node.querySelectorAll('.bi-non-exportable');
          non_exportable_elements.forEach((el) => {
            el.style.visibility = 'hidden';
          });

          const exportable_texts = node.querySelectorAll('.bi-exportable-text');
          exportable_texts.forEach((text) => {
            text.style.marginTop = '-14px';
          });

          const exportable_tags = node.querySelectorAll('.bi-exportable-tag');
          exportable_tags.forEach((tag) => {
            tag.style.marginTop = '14px';
            tag.style.minHeight = '14px';

            const children = tag.querySelectorAll('div');
            children.forEach((child) => {
              child.style.marginTop = '-14px';
            });
          });

          const tiptap_elements = node.querySelectorAll('.tiptap');
          tiptap_elements.forEach((el) => {
            const tables = el.getElementsByTagName('table');
            Array.from(tables).forEach((table) => {
              table.style.marginTop = '10px';
            });
            const tds = el.getElementsByTagName('td');
            Array.from(tds).forEach((td) => {
              td.style.paddingTop = '9.5px';
              td.style.paddingBottom = '9.5px';
            });

            const ths = el.getElementsByTagName('th');
            Array.from(ths).forEach((th) => {
              th.style.paddingTop = '9.5px';
              th.style.paddingBottom = '9.5px';
            });
          });

          const image_elements = node.querySelectorAll('img');
          await Promise.all(
            Array.from(image_elements).map(async (el) => {
              const src = el.src;

              if (!src || src.startsWith('data:') || src.startsWith('blob:'))
                return;

              try {
                // Fetch the image and convert it to a data URL so that html2canvas can draw it without any CORS restrictions (data URLs are same-origin).
                const response = await fetch(src, { mode: 'cors', cache: 'no-store' });
                if (!response.ok)
                  throw new Error(`HTTP ${response.status}`);

                const blob = await response.blob();
                el.src = await new Promise((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onload = () => resolve(reader.result);
                  reader.onerror = reject;
                  reader.readAsDataURL(blob);
                });
              }
              catch {
                el.crossOrigin = 'Anonymous';
              }
            }),
          );

          const vgl_layout = node.querySelector('.vgl-layout');
          vgl_layout.style.backgroundColor = '#ffffff';

          const dashboard_toolbar = node.querySelector('.bi-dashboard-toolbar');
          dashboard_toolbar.style.backgroundColor = '#ffffff';
          dashboard_toolbar.style.position = 'static';
          dashboard_toolbar.style.top = 'auto';

          const quickFilterTabs = node.querySelectorAll('.bi-widget-quick-filters .border-b-2');
          quickFilterTabs.forEach((tab) => {
            tab.style.height = '26px';
            tab.style.borderBottomColor = 'transparent';
          });

          const quickFilterDropdowns = node.querySelectorAll('.bi-widget-quick-filters .px-2\\.5');
          quickFilterDropdowns.forEach((dropdown) => {
            dropdown.style.height = '26px';
          });

          syncRowHeaderHeights(node);

          // Insert header at the beginning
          node.insertBefore(pdfHeader, node.firstChild);
        },
      });

      if (signal?.aborted) {
        throw new Error('Export cancelled by user');
      }

      // eslint-disable-next-line new-cap
      const pdf = new jsPDF({
        unit: 'px',
        compress: true,
      });

      pdf.deletePage(1);

      const scale = exportScale;
      const pageEnds = [...pageBreaks, contentHeight];
      let prevBreak = 0;

      for (let index = 0; index < pageEnds.length; index++) {
        if (signal?.aborted) {
          throw new Error('Export cancelled by user');
        }

        const pageBreak = pageEnds[index];
        const isLastPage = index === pageEnds.length - 1;
        const pageBreaksDiff = pageBreak - prevBreak;

        const pageHeight = !isLastPage
          ? pageBreaksDiff + 10
          : Math.max(pageBreaksDiff + 10, optimalPageHeight);

        pdf.addPage([width, pageHeight]);

        pdf.setFillColor(backgroundColor);
        pdf.rect(0, 0, width, pageHeight, 'F');

        const sourceY = prevBreak;
        const sourceHeight = pageBreaksDiff;

        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = contentWidth * scale;
        pageCanvas.height = sourceHeight * scale;
        const ctx = pageCanvas.getContext('2d');

        if (ctx) {
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';

          ctx.drawImage(
            image,
            0,
            sourceY * scale,
            contentWidth * scale,
            sourceHeight * scale,
            0,
            0,
            contentWidth * scale,
            sourceHeight * scale,
          );

          pdf.addImage(
            pageCanvas,
            'PNG',
            PAGE_PADDING,
            PAGE_PADDING,
            contentWidth,
            sourceHeight,
            undefined,
            'FAST',
          );
        }

        prevBreak = pageBreak;
        await yieldToMain();
      }

      if (signal?.aborted) {
        throw new Error('Export cancelled by user');
      }

      pdf.save(`${selected_dashboard_details.value.name}.pdf`);
    }
    catch (error) {
      if (error.message === 'Export cancelled by user') {
        logger.log('Dashboard export cancelled');
      }
      else {
        logger.error('Error exporting dashboard:', error);
      }
      throw error;
    }
    finally {
      // Restore the live DOM styles that were expanded for capture
      for (const saved of savedStyles.reverse()) {
        for (const [prop, val] of Object.entries(saved)) {
          if (prop === 'el')
            continue;
          saved.el.style[prop] = val || '';
        }
      }

      // Restore Handsontable virtualization settings
      for (const instance of htInstancesToRestore) {
        try {
          instance.updateSettings({ renderAllColumns: false, renderAllRows: false });
        }
        catch { /* instance may have been destroyed */ }
      }
    }
  }

  return {
    exportWidget,
    exportDashboard,
  };
}
